import { isRecommendTabApp } from "~/lib/applicationId";
import { normalizeScreenPathname } from "../screenPathname";
import { type PendingScreenView, setFirebaseCurrentScreen } from "./analytics";
import { FIREBASE_SCREEN_ID, type FirebaseScreenId } from "./types";

/**
 * ルート pathname → 画面ID の対応。
 * 画面ID一覧（依頼書 §4 + 修正）に無い画面は screen_view を送らない。
 * Sina に存在しない画面（find/ranking/call_history/waiting 等）は割り当て無しでOK。
 */
const EXACT_PATH_TO_SCREEN_ID: Record<string, FirebaseScreenId> = {
  "/recommend": FIREBASE_SCREEN_ID.RECOMMEND,
  "/search": FIREBASE_SCREEN_ID.SEARCH,
  "/board": FIREBASE_SCREEN_ID.BOARD,
  "/my-page/footprint": FIREBASE_SCREEN_ID.FOOTSTEP,
  "/gallery": FIREBASE_SCREEN_ID.GALLERY,
  "/my-page/bookmarks": FIREBASE_SCREEN_ID.FAVORITE_LIST,
  "/setting/likes": FIREBASE_SCREEN_ID.LIKE_LIST,
  "/fleamarket": FIREBASE_SCREEN_ID.FLEAMARKET,
  "/my-page": FIREBASE_SCREEN_ID.PROFILE_SELF,
  "/purchase": FIREBASE_SCREEN_ID.PAYWALL,
};

/** 1対1チャット画面（/messages/<conversationId>） */
const CHAT_PATH_PATTERN = /^\/messages\/[^/]+$/;

/** pathname に対応する画面IDを返す。一覧に無い画面は null */
export const getFirebaseScreenIdForPath = (
  pathname: string,
): FirebaseScreenId | null => {
  const normalized = normalizeScreenPathname(pathname);
  // さがす画面: おすすめ画面を別に持つアプリ（Carina / Renka Android）では「探す(find)」、
  // おすすめ画面を持たないアプリは従来どおりさがす画面がおすすめ(recommend)を兼ねる
  if (normalized === "/discover") {
    return isRecommendTabApp()
      ? FIREBASE_SCREEN_ID.FIND
      : FIREBASE_SCREEN_ID.RECOMMEND;
  }
  const exact = EXACT_PATH_TO_SCREEN_ID[normalized];
  if (exact) return exact;
  if (CHAT_PATH_PATTERN.test(normalized)) return FIREBASE_SCREEN_ID.CHAT;
  return null;
};

let lastTrackedPathname: string | null = null;
let pendingView: { pathname: string; view: PendingScreenView } | null = null;

/**
 * ルート遷移確定時（onResolved）に呼ぶ。現在画面を切り替え、screen_view を送信待ちにする（まだ送らない）。
 * search params のみの変化（同一 pathname）は画面遷移とみなさない。追跡対象外の画面では送信待ちを空にする。
 */
export const registerScreenViewForPath = (pathname: string): void => {
  const normalized = normalizeScreenPathname(pathname);
  if (normalized === lastTrackedPathname) return;
  lastTrackedPathname = normalized;

  const screenId = getFirebaseScreenIdForPath(normalized);
  pendingView = screenId
    ? { pathname: normalized, view: setFirebaseCurrentScreen(screenId) }
    : null;
};

/**
 * 画面の描画完了（または打ち切り・離脱）時に呼ぶ。
 * 登録済みの pathname と一致するときだけ送信内容を返し、送信待ちを空にする。追跡対象外の画面は null
 */
export const takePendingScreenView = (
  pathname: string,
): PendingScreenView | null => {
  if (
    !pendingView ||
    pendingView.pathname !== normalizeScreenPathname(pathname)
  ) {
    return null;
  }
  const { view } = pendingView;
  pendingView = null;
  return view;
};

/** テスト用: 直近の追跡 pathname と送信待ちをリセットする */
export const resetFirebaseScreenPathState = (): void => {
  lastTrackedPathname = null;
  pendingView = null;
};
